import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '@/api/client';
import { useLogin, useMe, useProviders } from '@/api/queries';
import { Logo } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/form';
import { LANGUAGES, currentLanguage } from '@/i18n';
import { cn } from '@/lib/cn';

const KNOWN_ERRORS = [
  'invalid_credentials',
  'rate_limited',
  'not_allowed',
  'google_cancelled',
  'google_failed',
  'google_disabled',
  'login_expired',
] as const;
type KnownError = (typeof KNOWN_ERRORS)[number];
const isKnownError = (code: string): code is KnownError => (KNOWN_ERRORS as readonly string[]).includes(code);

/** Solo rutas internas como destino tras entrar. */
const safeReturnTo = (value: string | null) =>
  value?.startsWith('/') && !value.startsWith('//') && !value.startsWith('/api/') ? value : '/';

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden>
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}

/** Fondo: un trozo de tapete con dos áreas y unas peanas, a modo de ilustración. */
function MatBackdrop() {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      preserveAspectRatio="xMidYMid slice"
      viewBox="0 0 40 24"
      aria-hidden
    >
      <defs>
        <pattern id="login-grid" width="1" height="1" patternUnits="userSpaceOnUse">
          <path d="M1 0H0V1" fill="none" stroke="var(--c-grid)" strokeOpacity="0.28" strokeWidth="0.03" />
        </pattern>
      </defs>
      <rect width="40" height="24" fill="var(--c-mat)" />
      <circle cx="30" cy="7" r="5" fill="#6a3fa0" fillOpacity="0.16" />
      <circle cx="33" cy="9" r="4" fill="#3f9a3a" fillOpacity="0.16" />
      <polygon points="6,18 12,15 12,21" fill="#e07a2b" fillOpacity="0.18" />
      <rect width="40" height="24" fill="url(#login-grid)" />
      {[
        [29, 8, '#3d7a48', 2],
        [26, 12, '#2b2b2b', 1],
        [34, 5, '#2b2b2b', 1],
        [4, 17, '#6d3a86', 1],
        [7, 20, '#2f5d8a', 1],
      ].map(([x, y, c, s]) => (
        <circle
          key={`${String(x)}-${String(y)}`}
          cx={Number(x) + Number(s) / 2}
          cy={Number(y) + Number(s) / 2}
          r={Number(s) / 2 - 0.08}
          fill={String(c)}
          fillOpacity="0.55"
        />
      ))}
    </svg>
  );
}

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = safeReturnTo(params.get('returnTo'));
  const { data: me } = useMe();
  const providers = useProviders();
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  if (me) return <Navigate to={returnTo} replace />;

  const urlError = params.get('error');
  const mutationError = login.error instanceof ApiError ? login.error.code : login.error ? 'generic' : null;
  const errorCode = mutationError ?? urlError;
  const errorText = errorCode
    ? isKnownError(errorCode)
      ? t(`auth.errors.${errorCode}`)
      : t('auth.errors.generic')
    : null;

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    login.mutate({ email, password }, { onSuccess: () => void navigate(returnTo, { replace: true }) });
  };

  const google = providers.data?.google ?? false;
  const password_ = providers.data?.password ?? false;

  return (
    <main className="relative flex min-h-full items-center justify-center overflow-hidden px-4 py-10">
      <MatBackdrop />
      <div className="relative w-full max-w-sm rounded-xl border border-line bg-surface/95 p-7 shadow-panel backdrop-blur-sm">
        <Logo className="mb-5" />
        <h1 className="font-display text-[1.7rem] leading-tight">{t('auth.title')}</h1>
        <p className="mt-1 mb-6 text-ink-2">{t('app.tagline')}</p>

        {errorText && (
          <p
            role="alert"
            className="mb-4 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-[0.92rem] text-danger"
          >
            {errorText}
          </p>
        )}

        {providers.isPending && <p className="text-ink-2">{t('common.loading')}</p>}

        {google && (
          <Button asChild variant="secondary" className="h-11 w-full justify-center text-base">
            <a href={`/api/auth/google?returnTo=${encodeURIComponent(returnTo)}`}>
              <GoogleMark />
              {t('auth.google')}
            </a>
          </Button>
        )}

        {google && password_ && (
          <div className="my-5 flex items-center gap-3 text-sm text-ink-3">
            <span className="h-px flex-1 bg-line" />
            {t('auth.or')}
            <span className="h-px flex-1 bg-line" />
          </div>
        )}

        {password_ && (
          <form onSubmit={submit} className="flex flex-col gap-3.5">
            <Field label={t('auth.email')}>
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('auth.password')}>
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
            <Button type="submit" variant="primary" className="mt-1 h-10 justify-center" disabled={login.isPending}>
              {login.isPending ? t('common.loading') : t('auth.submit')}
            </Button>
          </form>
        )}

        {providers.data && !google && !password_ && <p className="text-ink-2">{t('auth.noProviders')}</p>}

        <div className="mt-6 flex flex-wrap items-center justify-center gap-1 text-sm">
          {LANGUAGES.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => void i18n.changeLanguage(code)}
              className={cn(
                'rounded px-2 py-0.5',
                currentLanguage() === code ? 'bg-surface-2 text-ink' : 'text-ink-2 hover:text-ink',
              )}
            >
              {t(`language.${code}`)}
            </button>
          ))}
          <span className="mx-1 text-ink-3">·</span>
          <Link to="/privacidad" className="text-ink-2 underline-offset-2 hover:text-ink hover:underline">
            {t('privacy.link')}
          </Link>
        </div>
      </div>
    </main>
  );
}
